import { Box, Chip, Divider, Grid, Stack, Switch, Typography } from '@mui/material';
import { useIntl } from 'react-intl';
import { ClickableCard } from '@shared/components/ClickableCard';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import DetailColumn from '@shared/components/DetailColumn';
import { ImperativeFormDialogRefType } from '@shared/components/UserFormDialog';
import { UserData } from '@pages/profile';
import AvatarUploadButton from '../AvatarUploadButton';
import { useIntlTranslations } from '@libs/intl/intl.hooks';

type Props = {
  disabled?: boolean;
  userData: UserData;
  userEmail?: string;
  dialogRef: React.RefObject<ImperativeFormDialogRefType | null>;
  publicProfileUrl: string | null;
  publicCopied: boolean;
  onCopyPublicLink: () => void;
  onToggleReservations: (enabled: boolean) => void;
};

const PublicProfileSection = ({
  disabled = false,
  userData,
  userEmail,
  dialogRef,
  publicProfileUrl,
  publicCopied,
  onCopyPublicLink,
  onToggleReservations,
}: Props) => {
  const { t } = useIntlTranslations('publicProfileSection');
  const intl = useIntl();
  if (disabled) {
    const expertOnly = intl.formatMessage({
      id: 'visitDetailGrid.soldItemsRevenueDisabledMessage',
      defaultMessage: 'Dostupné v plánu EXPERT. Aktivujte ve vašem profilu.',
    });
    return (
      <>
        <Divider sx={{ my: 3 }} />
        <Box id="public-profile" sx={{ mb: 2 }}>
          <ClickableCard
            disabled
            disabledMessage={expertOnly}
            sxPaper={{ borderRadius: '14px', p: 2 }}
          >
            <Stack spacing={1}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t('title')}
                </Typography>
                <Chip label="EXPERT" size="small" variant="outlined" />
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {t('subtitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {expertOnly}
              </Typography>
            </Stack>
          </ClickableCard>
        </Box>
      </>
    );
  }
  const fields = [
    {
      id: 'publicProfileLink',
      label: t('linkLabel'),
      value: publicCopied ? t('linkCopied') : publicProfileUrl || t('linkUnavailable'),
      icon: <ContentCopyOutlinedIcon sx={{ color: '#fff' }} />,
      onClick: onCopyPublicLink,
    },
    {
      id: 'address',
      label: t('addressLabel'),
      value: userData.address || '—',
      icon: <LocationOnOutlinedIcon sx={{ color: '#fff' }} />,
      onClick: () => dialogRef.current?.open('address'),
    },
    {
      id: 'bio',
      label: t('bioLabel'),
      value: userData.bio || '—',
      icon: <InfoOutlinedIcon sx={{ color: '#fff' }} />,
      onClick: () => dialogRef.current?.open('bio'),
    },
    {
      id: 'phoneNumber',
      label: t('phoneLabel'),
      value: userData.phone || '—',
      icon: <PhoneOutlinedIcon sx={{ color: '#fff' }} />,
      onClick: () => dialogRef.current?.open('phone'),
    },
    {
      id: 'contactEmail',
      label: t('emailLabel'),
      value: userData.contactEmail || userEmail || '—',
      icon: <EmailOutlinedIcon sx={{ color: '#fff' }} />,
      onClick: () => dialogRef.current?.open('contactEmail'),
    },
  ];

  return (
    <>
      <Divider sx={{ my: 3 }} />
      <Box
        id="public-profile"
        sx={{
          borderRadius: '14px',
          border: '2px solid',
          borderColor: userData.allowPublicReservations !== false ? 'success.main' : 'divider',
          bgcolor: userData.allowPublicReservations !== false ? 'success.light' : '#fafafa',
          p: 2,
          mb: 2,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700} color="text.primary">
              {t('title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('subtitle')}
            </Typography>
          </Box>
          <Stack
            direction="row"
            alignItems="center"
            borderRadius="14px"
            sx={{
              p: 1,
              bgcolor: userData.allowPublicReservations !== false ? '#9eff8f' : '#ffc9c9',
            }}
          >
            <Typography variant="body2" fontWeight={700} fontSize="12px" color="text.primary">
              {userData.allowPublicReservations !== false ? t('enabled') : t('disabled')}
            </Typography>
            <Switch
              checked={userData.allowPublicReservations !== false}
              onChange={e => onToggleReservations(e.target.checked)}
            />
          </Stack>
        </Stack>

        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
          <AvatarUploadButton
            userId={userData.id}
            currentUrl={userData.avatarUrl}
            name={userData.name}
          />
        </Box>

        <Grid container rowSpacing={2} columnSpacing={2}>
          {fields.map(field => (
            <Grid key={field.id} size={12}>
              <DetailColumn
                onClick={field.onClick}
                label={field.label}
                iconBackgroundColor="#D02964"
                icon={field.icon}
                input={field.value}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
};

export default PublicProfileSection;
